import React, {useEffect, useState} from 'react'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import {useDispatch, useSelector} from 'react-redux'
import Resources from '../../../data/services/resources'
import {getSecondResource} from '../../../data/actions/secondResource'
import PayDateFilter from '../../accounting/carrier-pay/carrier-pay-date-filter'
import {getProp} from '../../../common/util/util-helpers'
import {download} from '../../../data/actions/download'
import {currentDate} from '../../../common/util/util-dates'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'
import Tippy from '@tippyjs/react'
import TableFilters from '../../../common/components/resource-table/table-components/table-filters'
import ActiveFilters from '../../../common/components/resource-table/table-components/active-filters'
import {excludeFields} from '../../../common/util/util-fields'
import moment from 'moment'
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY} from '../../../common/util/util-consts'
import CostPerMileTrend from './cost-per-mile-trend'
import {showGlobalModal} from '../../../data/actions/ui'
import CostPerCodeDialog from './cost-per-code-dialog'

const yearSelectOptions = (() => {
    const opts = {'': 'All years'}
    const current = new Date().getFullYear()
    for (let y = current + 1; y >= 1990; y--) opts[String(y)] = String(y)
    return opts
})()

const getTruckSelects = () => ({
    TruckIDs: {
        api: 'api/' + Resources.TrucksQuick,
        query: Object.assign(DEFAULT_METADATA_SELECT_SEARCH_QUERY(), {NotSoldRetired: 1}),
        searchMap: (item) => ({value: item.TruckID, label: item.TruckNumber})
    },
    ScheduledMaintenanceIDs: {
        api: 'api/' + Resources.WorkOrderScheduledMaintenance,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({
            value: item.ScheduledMaintenanceID,
            label: item.ScheduledMaintenanceType + (item.ScheduleNotes ? ', ' + item.ScheduleNotes : '')
        })
    },
    Year: yearSelectOptions,
    OfficeID: {
        api: 'api/' + Resources.OfficesQuick,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({value: item.OfficeID, label: item.OfficeName})
    },
})

const getTrailerSelects = () => ({
    TrailerIDs: {
        api: 'api/' + Resources.TrailersQuick,
        query: Object.assign(DEFAULT_METADATA_SELECT_SEARCH_QUERY(), {NotSoldRetired: 1}),
        searchMap: (item) => ({value: item.TrailerID, label: item.TrailerNumber})
    },
    ScheduledMaintenanceIDs: {
        api: 'api/' + Resources.WorkOrderScheduledMaintenance,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({
            value: item.ScheduledMaintenanceID,
            label: item.ScheduledMaintenanceType + (item.ScheduleNotes ? ', ' + item.ScheduleNotes : '')
        })
    },
    Year: yearSelectOptions,
    OfficeID: {
        api: 'api/' + Resources.OfficesQuick,
        query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
        searchMap: (item) => ({value: item.OfficeID, label: item.OfficeName})
    },
})

const WO_REPORTS_DATES_KEY = 'wo_reports_dates'
const defaultStart = () => moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
const defaultEnd = () => moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
const getSavedDates = () => LocalStorage.get(WO_REPORTS_DATES_KEY) || {}

const getQueryFields = (isTrailer) => {
    const saved = getSavedDates()
    return {
    StartDate: new Field('StartDate', saved.StartDate || defaultStart(), [], false, 'date', {}, {}),
    EndDate: new Field('EndDate', saved.EndDate || defaultEnd(), [], false, 'date', {}, {}),
    ...(isTrailer
        ? {TrailerIDs: new Field('TrailerIDs', '', [''], false, 'select-search', {}, {multi: true, all: true})}
        : {TruckIDs: new Field('TruckIDs', '', [''], false, 'select-search', {}, {multi: true, all: true})}
    ),
    ScheduledMaintenanceIDs: new Field('ScheduledMaintenanceIDs', '', [''], false, 'select-search', {}, {multi: true, all: true}),
    NotSoldRetired: new Field('NotSoldRetired', '1', [''], false, 'checkbox', {}),
    OfficeID: new Field('OfficeID', '', [''], false, 'select-search', {}, {isClearable: true}),
    Year: new Field('Year', '', [''], false, 'select', {}, {isClearable: true}),
    Manufacturer: new Field('Manufacturer', '', [''], false, 'text', {}),
    Model: new Field('Model', '', [''], false, 'text', {}),
}}

export default function CostPerMileReportTab({translate}) {

    /** Store
     ================================================================= */
    const dispatch = useDispatch()
    const secondResource = useSelector((state) => state.secondResource)
    const isLoading = secondResource.isLoading
    const periods = secondResource?.data?.periods ?? []
    const list = secondResource?.data?.list ?? []
    const count = getProp(secondResource.data, 'count', 0)

    /** State
     ================================================================= */
    const [isTrailerMode, setIsTrailerMode] = useState(false)
    const [queryFields, setQueryFields] = useState(getQueryFields(false))
    const [perCodeUnit, setPerCodeUnit] = useState(null)

    /** Helpers
     ================================================================= */
    const getQuery = () => {
        const base = FieldsManager.getFieldKeyValues(queryFields)
        if (isTrailerMode) base.IsTrailer = 1
        return base
    }

    /** Data events
     ================================================================= */
    const fetchData = () => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: Resources.WorkOrdersReportsMaintenanceCostMileage,
            query: getQuery()
        }))
    }

    const downloadExcel = () => {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: Resources.WorkOrdersReportsMaintenanceCostMileage,
            query: Object.assign({
                format: 'EXCEL',
                name: 'cost_per_mile_report_' + currentDate() + '.xlsx'
            }, getQuery())
        }))
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        fetchData()
    }, [queryFields, isTrailerMode])

    /** UI events
     ================================================================= */
    const handleQueryChange = (name, value) => {
        let fields = FieldsManager.updateField(queryFields, name, value)

        if (name === 'StartDate') fields.EndDate.props.minDate = value
        if (name === 'EndDate') fields.StartDate.props.maxDate = value

        setQueryFields(fields)

        if (name === 'StartDate' || name === 'EndDate') {
            LocalStorage.set(WO_REPORTS_DATES_KEY, {...getSavedDates(), [name]: value})
        }
    }

    const handleClearFiltersClick = () => {
        const fieldsTmp = Object.assign({}, queryFields)
        Object.values(fieldsTmp).forEach((it) => {
            FieldsManager.updateField(fieldsTmp, it.name, '')
        })
        setQueryFields(fieldsTmp)
    }

    const handleModeSwitch = (trailer) => {
        if (trailer === isTrailerMode) return
        setIsTrailerMode(trailer)
        setQueryFields(getQueryFields(trailer))
    }

    const handleViewTruckClick = (id) => {
        dispatch(showGlobalModal('ViewTruckCard', id))
    }

    const handleViewTrailerClick = (id) => {
        dispatch(showGlobalModal('ViewTrailerCard', id))
    }

    const getDateFilterLabel = () => {
        const {StartDate, EndDate} = queryFields
        if (!StartDate.value || !EndDate.value) return translate('text.select_date_range') || 'Select dates'
        const fmt = (d) => new Date(d).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
        return `${fmt(StartDate.value)} – ${fmt(EndDate.value)}`
    }

    const selects = isTrailerMode ? getTrailerSelects() : getTruckSelects()

    /** Render
     ================================================================= */
    return (
        <div>
            <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card">
                <div className="flex items-center justify-between px-4 pt-2">
                    <span className="text-xs text-tm-gray-400">{getDateFilterLabel()}</span>

                    <div className="flex items-center rounded-lg border border-tm-gray-300 overflow-hidden text-sm font-medium">
                        <button
                            className={`px-3 py-1 transition-colors ${!isTrailerMode ? 'bg-primary text-white' : 'text-tm-gray-600 hover:bg-tm-gray-100'}`}
                            onClick={() => handleModeSwitch(false)}
                        >
                            Trucks
                        </button>
                        <button
                            className={`px-3 py-1 transition-colors ${isTrailerMode ? 'bg-primary text-white' : 'text-tm-gray-600 hover:bg-tm-gray-100'}`}
                            onClick={() => handleModeSwitch(true)}
                        >
                            Trailers
                        </button>
                    </div>
                </div>

                <div className="sm:flex justify-between items-start">
                    <ActiveFilters
                        addClass="px-4 pt-2"
                        filterFields={queryFields}
                        onLabelClick={handleQueryChange}
                        persistFilters={['StartDate', 'EndDate']}
                        onClearFiltersClick={handleClearFiltersClick}
                        translate={translate}
                    />

                    <div className="px-4 pt-2 flex-shrink-0">
                        <Tippy content={translate('text.download_excel')}>
                            <button className="btn-icon" onClick={downloadExcel}>
                                <DocumentArrowDownIcon className="w-5 h-5"/>
                            </button>
                        </Tippy>
                    </div>
                </div>

                <TableFilters
                    forceDialog
                    hideLimit
                    filterFields={excludeFields(queryFields, ['StartDate', 'EndDate'])}
                    handleInputChange={handleQueryChange}
                    translate={translate}
                    onRefreshTable={fetchData}
                    selects={selects}
                    isLoading={isLoading}
                    customHtml={
                        <PayDateFilter
                            queryFields={queryFields}
                            hasDateType={false}
                            translate={translate}
                            updateQueryFields={(qf) => {
                                setQueryFields(qf)
                                LocalStorage.set(WO_REPORTS_DATES_KEY, {
                                    StartDate: qf.StartDate?.value,
                                    EndDate: qf.EndDate?.value,
                                })
                            }}
                            onQueryChange={handleQueryChange}
                        />
                    }
                />
            </div>

            <CostPerMileTrend
                periods={periods}
                list={list}
                count={count}
                isLoading={isLoading}
                isTrailerMode={isTrailerMode}
                translate={translate}
                onViewTruckClick={handleViewTruckClick}
                onViewTrailerClick={handleViewTrailerClick}
                onUnitClick={(id, name) => setPerCodeUnit({id, name})}
            />

            <CostPerCodeDialog
                show={!!perCodeUnit}
                onClose={() => setPerCodeUnit(null)}
                unitId={perCodeUnit?.id}
                unitName={perCodeUnit?.name}
                isTrailerMode={isTrailerMode}
                startDate={queryFields.StartDate?.value}
                endDate={queryFields.EndDate?.value}
                translate={translate}
            />
        </div>
    )
}
